import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LuFileQuestion } from 'react-icons/lu';
import { WorkspaceLayout } from '../workspace/WorkspaceLayout';
import { WorkspaceHeader } from '../workspace/WorkspaceHeader';
import { WorkspaceSkeleton } from '../workspace/WorkspaceSkeleton';
import { StatementPane } from '../workspace/StatementPane';
import { ProblemStatement } from '../workspace/ProblemStatement';
import { CodeEditor } from '../workspace/CodeEditor';
import { RunConsole } from '../workspace/RunConsole';
import { RunSubmitButtons } from '../workspace/RunSubmitButtons';
import { SubmissionHistory } from '../workspace/SubmissionHistory';
import { ButtonLink, Button } from '../../ui/Button';
import { EmptyState } from '../../ui/Feedback';
import { useToast } from '../../ui/Toast';
import { celebrate } from '../../ui/Celebration';
import { useCatalog } from '../../stores/catalog';
import { useDocumentTitle, useStrings } from '../../tenant/TenantProvider';
import { useCodeWorkspace } from '../../hooks/useCodeWorkspace';
import { useNow } from '../../hooks/useNow';
import { practiceApi } from '../../lib/practiceApi';
import { courseApi } from '../../lib/courseApi';
import { acceptanceRate } from '../../lib/format';
import { storage } from '../../lib/storage';

export const LAST_PROBLEM_KEY = 'last-problem';

/**
 * The practice workspace. Under /courses/:courseId/problems/:problemId it
 * submits through the course, so the solve counts toward that course, and
 * shows the course's own status and submissions.
 */
export function ProblemPage() {
  const { problemId, courseId } = useParams();
  const t = useStrings();
  const { show } = useToast();
  const now = useNow(1000);
  const latestRequest = useRef(0);
  const [state, setState] = useState({ status: 'loading', problem: null, tags: [], error: null });
  const [panelTab, setPanelTab] = useState('description');

  const load = useCallback(async () => {
    const request = latestRequest.current + 1;
    latestRequest.current = request;
    setState((current) => ({ ...current, status: 'loading' }));
    const [problemResponse, tagsResponse, courseResponse] = await Promise.all([
      practiceApi.problem(problemId),
      practiceApi.tags(problemId),
      courseId ? courseApi.get(courseId) : null,
    ]);
    if (latestRequest.current !== request) return;
    if (courseResponse && !courseResponse.success) {
      setState({ status: 'error', problem: null, tags: [], error: courseResponse.status === 404 ? t('courses.notFound') : courseResponse.error });
      return;
    }
    const courseQuestion = courseResponse?.data.questions.find((question) => String(question.id) === problemId);
    if (courseResponse && !courseQuestion) {
      setState({ status: 'error', problem: null, tags: [], error: t('courses.problemNotFound') });
      return;
    }
    if (!problemResponse.success) {
      setState({ status: 'error', problem: null, tags: [], error: problemResponse.status === 404 ? t('workspace.notFound') : problemResponse.error });
      return;
    }
    // In a course, "solved" means solved through this course.
    const problem = courseQuestion ? { ...problemResponse.data, solved: courseQuestion.status === 'solved' } : problemResponse.data;
    setState({ status: 'ready', problem, tags: tagsResponse.success ? tagsResponse.data : [], error: null });
    setPanelTab('description');
    storage.setJson(LAST_PROBLEM_KEY, { id: problemResponse.data.id, title: problemResponse.data.title });
  }, [problemId, courseId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const problem = state.status === 'ready' ? state.problem : null;
  useDocumentTitle(problem?.title ?? t('nav.problems'));

  const handleJudged = (_, result) => {
    if (result.verdict !== 'Accepted') return;
    setState((current) => (current.problem && !current.problem.solved ? { ...current, problem: { ...current.problem, solved: true } } : current));
    celebrate(document.querySelector('[data-celebrate-origin]'));
    if (problem) useCatalog.getState().markSolved(problem.id);
    show(t('workspace.acceptedToast'));
  };

  const api = useMemo(
    () => (courseId ? { ...practiceApi, submit: (id, body) => courseApi.submit(courseId, id, body) } : practiceApi),
    [courseId]
  );

  const workspace = useCodeWorkspace({
    draftPrefix: 'practice',
    problemId: problem ? problem.id : null,
    problem,
    canAct: Boolean(problem),
    now,
    api,
    onJudged: handleJudged,
  });

  const loadSubmissions = useCallback(
    () => (courseId ? courseApi.submissions(courseId, problemId) : practiceApi.submissions(problemId)),
    [courseId, problemId]
  );
  const backTo = courseId ? `/courses/${courseId}` : '/problems';
  const rate = useMemo(() => (problem ? acceptanceRate(problem.acceptedCount, problem.submissionCount) : null), [problem]);

  if (state.status === 'loading' && !problem) return <WorkspaceSkeleton />;

  if (state.status === 'error') {
    return (
      <div className="not-found">
        <EmptyState
          icon={LuFileQuestion}
          title={state.error}
          action={
            <div style={{ display: 'flex', gap: 8 }}>
              <Button onClick={load}>{t('common.retry')}</Button>
              <ButtonLink to={backTo} variant="primary">
                {courseId ? t('courses.back') : t('common.goToProblems')}
              </ButtonLink>
            </div>
          }
        />
      </div>
    );
  }

  return (
    <WorkspaceLayout
      header={
        <WorkspaceHeader
          backTo={backTo}
          backLabel={courseId ? t('courses.backToCourse') : t('workspace.backToProblems')}
          title={`${problem.id}. ${problem.title}`}
          actions={
            <RunSubmitButtons
              canAct
              running={workspace.running}
              submitting={workspace.judging}
              runCooldownMs={workspace.runCooldownMs}
              submitCooldownMs={workspace.submitCooldownMs}
              onRun={workspace.handleRun}
              onSubmit={workspace.handleSubmit}
            />
          }
        />
      }
      statement={
        <StatementPane tab={panelTab} onTabChange={setPanelTab}>
          {panelTab === 'description' ? (
            <ProblemStatement
              number={`${problem.id}.`}
              title={problem.title}
              difficulty={problem.difficulty}
              meta={rate === null ? [] : [t('workspace.acceptance', { rate })]}
              solved={problem.solved}
              description={problem.description}
              topics={state.tags}
            />
          ) : (
            <SubmissionHistory load={loadSubmissions} version={workspace.submissionsVersion} />
          )}
        </StatementPane>
      }
      editor={
        <CodeEditor
          code={workspace.code}
          onChange={workspace.updateCode}
          language={workspace.language}
          languages={workspace.languages}
          onLanguageChange={workspace.changeLanguage}
          onReset={workspace.resetCode}
          onRun={workspace.handleRun}
          onSubmit={workspace.handleSubmit}
          savedAt={workspace.savedAt}
        />
      }
      console={
        <RunConsole
          testCases={workspace.testCases}
          onTestCasesChange={workspace.updateCases}
          onResetCases={workspace.resetCases}
          tab={workspace.consoleTab}
          onTabChange={workspace.setConsoleTab}
          run={workspace.run}
          submission={workspace.judgement}
          lastAction={workspace.lastAction}
        />
      }
    />
  );
}
